import { COLORS } from './colors';
import { GRID_GAP } from './tileGenerator';

/** Must match `borderRadius` on grid tiles in `Item.tsx`. */
export const TILE_BORDER_RADIUS = 8;

/** Width/height of match indicators in grid gaps — thicker than `GRID_GAP` for visibility. */
export const CONNECTOR_THICKNESS = 6;

const centerInGap = (gapStart: number, gapSize: number, thickness: number): number =>
  gapStart + (gapSize - thickness) / 2;

/** Visible viewport — accounts for mobile browser chrome (iOS Safari toolbars). */
export const getVisibleViewportSize = (): { width: number; height: number } => {
  const vv = window.visualViewport;
  return {
    width: vv?.width ?? window.innerWidth,
    height: vv?.height ?? window.innerHeight,
  };
};

/** Non-grid UI chrome on mobile portrait (title, nav, solutions, buttons, gaps). */
const MOBILE_CHROME_BUDGET = 320;
const GRID_ROWS = 3;

export const getTileDisplayWidth = (): number => {
  const mobileProportions = window.outerWidth < window.outerHeight * 0.75;

  if (!mobileProportions) {
    return Math.min(window.outerHeight * 0.19, 250);
  }

  const { width, height } = getVisibleViewportSize();
  const widthBased = width * 0.3;
  const heightBased = (height - MOBILE_CHROME_BUDGET) / GRID_ROWS;
  return Math.min(widthBased, heightBased, 250);
};

export type GapConnector = {
  id: string;
  left: number;
  top: number;
  width: number;
  height: number;
  matched: boolean;
};

const pushConnector = (
  connectors: GapConnector[],
  connector: GapConnector
) => {
  connectors.push(connector);
};

/** One indicator per shared border in each grid gap — green if both edge chars match, red otherwise. */
export const getGapConnectors = (
  tiles: string[],
  columns: number,
  tileSize: number,
  gap: number = GRID_GAP,
  connectorThickness: number = CONNECTOR_THICKNESS
): GapConnector[] => {
  if (tiles.length === 0) return [];

  const connectors: GapConnector[] = [];
  const w = tileSize;
  const h = tileSize;
  const inset = TILE_BORDER_RADIUS / 2;

  for (let i = 0; i < tiles.length; i++) {
    const col = i % columns;
    const row = Math.floor(i / columns);
    const tile = tiles[i];

    if (col < columns - 1) {
      const neighbor = tiles[i + 1];
      if (!neighbor) continue;
      const gapX = (col + 1) * w + col * gap;

      pushConnector(connectors, {
        id: `v-${i}`,
        left: centerInGap(gapX, gap, connectorThickness),
        top: row * (h + gap) + inset,
        width: connectorThickness,
        height: Math.max(0, h - 2 * inset),
        matched:
          tile.charAt(3) === neighbor.charAt(0) &&
          tile.charAt(4) === neighbor.charAt(7),
      });
    }

    if (i + columns < tiles.length) {
      const neighbor = tiles[i + columns];
      if (!neighbor) continue;
      const gapY = (row + 1) * h + row * gap;

      pushConnector(connectors, {
        id: `h-${i}`,
        left: col * (w + gap) + inset,
        top: centerInGap(gapY, gap, connectorThickness),
        width: Math.max(0, w - 2 * inset),
        height: connectorThickness,
        matched:
          tile.charAt(6) === neighbor.charAt(1) &&
          tile.charAt(5) === neighbor.charAt(2),
      });
    }
  }

  return connectors;
};

export const connectorColor = (matched: boolean): string =>
  matched ? COLORS.MATCH_CONNECTOR : COLORS.MISMATCH_CONNECTOR;

export const getGridPixelSize = (
  tileCount: number,
  columns: number,
  tileSize: number,
  gap: number = GRID_GAP
): { width: number; height: number } => {
  const rows = Math.ceil(tileCount / columns);
  return {
    width: columns * tileSize + (columns - 1) * gap,
    height: rows * tileSize + (rows - 1) * gap,
  };
};
